<template>
  <div>
    <transition name="fade">
      <GuideVideoBanner
        v-if="guideData && showGuideCard"
        :text="guideData.content.text"
        :subtext="guideData.content.subText"
        :videoUrl="guideData.content.videoUrl"
        :videoThumbnail="guideData.content.videoThumbnail"
        @close="handleGuideClose"
      />
    </transition>
    <EmployeesHeaderDashboard
      v-if="ideaMDP556"
      class="mb-4"
      :active-count="employeeStore.headerCounts.activeCount"
      :no-access-count="employeeStore.headerCounts.noAccessCount"
      :draft-count="employeeStore.headerCounts.draftCount"
      :active-no-primary-position-count="employeeStore.headerCounts.activeNoPrimaryPositionCount"
      :active-no-prepare-for-count="employeeStore.headerCounts.activeNoPrepareForCount"
      :active-indicator="activeIndicatorKey"
      :show-prepare-for-card="readinessProductEnabled"
      @select-indicator="handleIndicatorSelect"
    />
    <EmployeesWithNoAccessAlertBanner
      v-else-if="showEmployeesWithNoAccessBanner"
      :activeEmployeesWithNoaccess="activeEmployeesWithNoaccess"
      :description="description"
      :actionLabel="actionLabel"
      @isFilter="handleFilterClick"
    />
    <div class="org-employee-tab">
      <EmployeesOrganizationTable
        :employees="props.employees"
        ref="employeesTableRef"
        @drilldown="handleEmployeeDrilldown"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, watch, computed, getCurrentInstance } from "vue";
import EmployeesOrganizationTable from "@components/EmployeesOrganizationTable/EmployeesOrganizationTable.vue";
import EmployeesWithNoAccessAlertBanner from "@components/EmployeesWithNoAccessAlertBanner/EmployeesWithNoAccessAlertBanner.vue";
import EmployeesHeaderDashboard from "@components/EmployeesHeaderDashboard/EmployeesHeaderDashboard.vue";
import { useEmployeesOrganizationTableViewState } from "@components/EmployeesOrganizationTable/composables/useEmployeesOrganizationTableViewState";
import GuideVideoBanner from "@components/GuideVideoBanner/GuideVideoBanner.vue";
import { useKnockGuides } from "@/composables/common/useKnockGuides";
import { refreshAccountCounts } from "@/utils/accountCountUtils";
import { useEmployeeStore } from "@/store/pinia/employee";
import { HEADER_DASHBOARD_FLAGS } from "@components/constants/launchDarklyFlagKeys";
import { isProductReadinessEnabled } from "@/utils/accountProductFlags";

const emit = defineEmits(["drilldown"]);

const props = defineProps({
  employees: {
    type: Array,
    default: () => [],
  },
  activeEmployeesWithNoaccess: {
    type: Number,
    default: 0,
  },
  description: {
    type: String,
    default: "",
  },
  actionLabel: {
    type: String,
    default: "Apply Filter",
  },
  mockGuideData: {
    type: Object,
    default: null,
  },
});

const viewState = useEmployeesOrganizationTableViewState();
const { activeIndicatorKey, selectIndicator: handleIndicatorSelect } = viewState;

const employeeStore = useEmployeeStore();

const instance = getCurrentInstance();
const { proxy } = instance;

/**
 * ideaMDP450 only applies when `activeEmployeesWithNoaccess === 0`:
 * - count > 0 → always show (flag ignored)
 * - count === 0 && flag on → hide
 * - count === 0 && flag off → show
 */
const ideaMDP450 = computed(
  () => Boolean(proxy?.$store?.getters?.["featureFlags/ideaMDP450"]),
);

/**
 * MDP-556 / ER-11654 — header dashboard of five count-only tiles.
 * When enabled, the standalone no-access banner is replaced by the dashboard's
 * "No Access" tile (which applies Status = Active + Access = No Access).
 */
const ideaMDP556 = computed(
  () => Boolean(proxy?.$store?.getters?.[HEADER_DASHBOARD_FLAGS.employees.storeGetter]),
);

/** The prepare-for tile only applies when product Readiness is on (same gate as the filter group). */
const readinessProductEnabled = computed(() => isProductReadinessEnabled());

const showEmployeesWithNoAccessBanner = computed(() => {
  const n = props.activeEmployeesWithNoaccess;
  if (n > 0) return true;
  return !ideaMDP450.value;
});

function filterValues(values) {
  if (!Array.isArray(values)) return [];
  return values.map((f) =>
    typeof f === "object" && f && "value" in f ? f.value : f,
  );
}

function hasNoAccessAndActiveFilters(values) {
  const v = filterValues(values).map((x) =>
    typeof x === "string" ? x.toLowerCase() : x,
  );
  return v.includes("no_access") && v.includes("active");
}

let activeCountsTimer = null;
watch(
  () => viewState.state.filters.values,
  () => {
    if (!hasNoAccessAndActiveFilters(viewState.state.filters.values)) return;
    clearTimeout(activeCountsTimer);
    activeCountsTimer = setTimeout(async () => {
      if (!hasNoAccessAndActiveFilters(viewState.state.filters.values)) return;
      let accountId;
      try {
        accountId = JSON.parse(
          localStorage.getItem("currentAccountID") || "null",
        );
      } catch {
        return;
      }
      if (accountId == null || accountId === "") return;
      await refreshAccountCounts(accountId);
    }, 200);
  },
  { deep: true },
);

onBeforeUnmount(() => clearTimeout(activeCountsTimer));

const guideData = ref(null);
const showGuideCard = ref(true);

const { selectGuide, subscribeToGuides } = useKnockGuides();
let unsubscribeGuide = null;

const refreshGuide = () => {
  const employeesGuide = selectGuide({ key: "employees-and-position-assignments" });
  guideData.value = employeesGuide?.steps?.[0] || null;
};

const initializeKnock = () => {
  unsubscribeGuide = subscribeToGuides(refreshGuide);
  refreshGuide();
};

const handleGuideClose = async () => {
  showGuideCard.value = false;

  if (guideData.value && guideData.value.markAsArchived) {
    try {
      await guideData.value.markAsArchived();
    } catch (error) {
      console.error("Error archiving guide in Knock:", error);
    }
  }
};

onMounted(async () => {
  if (props.mockGuideData) {
    guideData.value = props.mockGuideData;
  } else {
    initializeKnock();
  }
});

onBeforeUnmount(() => {
  if (typeof unsubscribeGuide === "function") {
    unsubscribeGuide();
    unsubscribeGuide = null;
  }
});

const handleFilterClick = () => {
  const noAccessFilter = { label: "No Access", value: "no_access" };
  const activeStatusFilter = { label: "Active", value: "active" };
  const currentFilters = viewState.state.filters.values || [];

  const hasNoAccessFilter = currentFilters.some(
    (f) => (typeof f === "object" ? f.value : f) === "no_access",
  );

  if (!hasNoAccessFilter) {
    // When applying filter from banner, replace all existing filters (including defaults)
    // with only "no_access" and "active" filters to show only active employees without access
    // This removes default filters like "draft" that might be present
    const newFilters = [noAccessFilter, activeStatusFilter];

    viewState.applyFilters(newFilters);
  } else {
    viewState.state.filters.showFilterDialog = true;
  }
};

const handleEmployeeDrilldown = (data) => {
  emit("drilldown", data);
};
</script>

<style scoped>
.org-employee-tab {
  margin-top: 15px;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

::v-deep .guide-video-banner {
  margin-bottom: 15px;
}
</style>
